import { Select } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../components/Button/Button';
import { sortByHighestSeverity } from '../../logic/store/Insights/InsightsModels';
import { useAppSelector } from '../../logic/store/Store';
import { ReportView } from './Report';
import { SingleRow } from './SingleRow';
import { FeedbackThumbs } from './Feedback';
import { ArrowBackIcon } from './Icons/ArrowBackIcon';
import { SectionHeader } from './Components/SectionHeader';

const StyledDateRowContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	// mobile
	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		.ant-select {
			width: 100% !important;
		}
	}
`;


interface ISellerViewProps
{
	backToLastView: () => void;
	onSelectGrower: (Id: string) => void;
	changeSnapshotDate: (Id: string, snapshotDate: string) => void;
}

export const SellerView = (props: ISellerViewProps) =>
{
	const theme = useTheme();

	const { backToLastView, onSelectGrower, changeSnapshotDate } = props;

	const aiexperience = useAppSelector(s => s.insights.aiExperience);
	const selectedSellerId = useAppSelector(s => s.insights.currentSellerId);
	const selectedSellerAnalysis = useAppSelector(s => s.insights.singleSellerAnalysis);
	const sellerAnalysesCount = useAppSelector(s => s.insights.sellerMemberAnalyses.length);

	// Sort the growers by severity
	const sortedGrowers = useMemo(() =>
	{
		if (!selectedSellerAnalysis || selectedSellerAnalysis.GrowerAnalyses == null || selectedSellerAnalysis.GrowerAnalyses.length === 0)
		{
			return [];
		}

		return sortByHighestSeverity(selectedSellerAnalysis.GrowerAnalyses, g => g.Report?.Concerns || [], g => g.GrowerName);
	}, [selectedSellerAnalysis]);

	// Change the troubleshooting tool's snapshot date
	const onChangeSnapshotDate = (date: string) =>
	{
		if (!date)
		{
			return;
		}
		
		changeSnapshotDate(selectedSellerId, date);
	};

	const startYear = (new Date().getFullYear()-1).toString();

	return (
		<div 
			style={{ 
				display: 'flex', 
				flexDirection: 'column',
				width: '100%', 
				height: '98%', 
				overflow: 'hidden',
			}}>
			<SectionHeader>
				{
					(aiexperience === 'SalesRep' || aiexperience === 'MultiSalesRep'
						|| (aiexperience === 'Seller' && sellerAnalysesCount > 1)) 
					&& (
						<Button 
							variant='light' 
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 32,
								width: 32,
							}}
							title='Back to Seller List'
							onClick={backToLastView}
						>
							<ArrowBackIcon style={{transform: 'translateY(-1px)'}} />
						</Button>
					)}
					Seller - {selectedSellerAnalysis?.SellerName}
			</SectionHeader>
			<div style={{ 
				display: 'flex', 
				overflowY: 'auto', 
				flexDirection: 'column', 
				padding: '20px 0px',
			}}>
				<StyledDateRowContainer>
					<span style={{ fontWeight: theme.fontWeights.bold, minWidth: 90 }}>Report Date:</span>
					<Select value={selectedSellerAnalysis?.SnapshotDate} style={{ width: 150 }} onChange={onChangeSnapshotDate}>
						{
							selectedSellerAnalysis?.AvailableDates?.filter(ad => ad > startYear).sort().reverse().map(date => (
								<Select.Option key={date} value={date}>
									{moment(date, 'YYYY-MM-DD').format('L')}
								</Select.Option>
							))
						}
					</Select>
				</StyledDateRowContainer>
				<div style={{ width: '95%', display: 'flex', flexDirection: 'row', marginTop: 10, marginBottom: 20, gap: 6 }}>
					<div style={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
						<div style={{ fontWeight: theme.fontWeights.bold, marginBottom: 5 }}>
							Overview:
						</div>
						<FeedbackThumbs report={selectedSellerAnalysis?.Report} />
					</div>
					<div style={{ marginLeft: 10 }}>{selectedSellerAnalysis?.Report?.Overview}</div>
				</div>
				<ReportView report={selectedSellerAnalysis?.Report} />
				{
					sortedGrowers?.length > 0 &&
					<div style={{ marginTop: 20 }}>
						<SectionHeader>
							Growers
						</SectionHeader>
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 20 }}>
							{
								sortedGrowers?.map((grower) =>
								{
									return (
										<SingleRow
											key={grower.item.GrowerId}
											Id={grower.item.GrowerId}
											onSelect={onSelectGrower}
											Name={grower.item.GrowerName}
											NumberOfItems={0}
											NumberOfItemsTitle={''}
											Severity={grower.highestSeverity}
											SeverityCount={grower.count}
										/>
									);
								})
							}
						</div>
					</div>
				}
			</div>
		</div>
	);
};
